import React from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { create_payment } from "services/apiSrv";
import { useParams } from "react-router-dom";
import { show_toast } from "utils/helpers";


// Validation schema
const validationSchema = Yup.object().shape({
  partPaymentAllowed: Yup.string().required("Part payment selection is required"),
  minAmount: Yup.number().when("partPaymentAllowed", {
    is: "yes",
    then: (schema) =>
      schema.required("Minimum amount is required").min(0, "Minimum amount must be non-negative"),
  }),
  minPeriod: Yup.number().when("partPaymentAllowed", {
    is: "yes",
    then: (schema) =>
      schema.required("Minimum period is required").min(1, "Minimum period must be at least 1 day"),
  }),
  maxPeriod: Yup.number().when("partPaymentAllowed", {
    is: "yes",
    then: (schema) =>
      schema
        .required("Maximum period is required")
        .min(Yup.ref("minPeriod"), "Maximum period must be greater than or equal to minimum period"),
  }),
  parts: Yup.array().of(
    Yup.object().shape({
      period: Yup.number().required("Period is required").min(1, "Period must be at least 1 day"),
      value: Yup.number().required("Value is required").min(0, "Value must be non-negative"),
    })
  ),
});

export default function PartPaymentForm({refetchProductDetails}) {
  const { id: productSystemRef } = useParams();

  const initialValues = {
    partPaymentAllowed: "no",
    minAmount: 0,
    maxAmount: 0,
    minPeriod: 0,
    maxPeriod: 0,

    parts: [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    try {
      const payload = {
        productSystemRef: productSystemRef,
        part_payment_allowed: values.partPaymentAllowed ==='yes',
        ...(values.partPaymentAllowed === "yes" ? {
          min_amount: Number(values.minAmount),
          max_amount: Number(values.maxAmount),
          min_period: Number(values.minPeriod),
          max_period: Number(values.maxPeriod),
          parts: values.parts.map((part) => ({
            period: Number(part.period),
            value: Number(part.value),
          })),
        }:{
          min_amount: Number(values.minAmount),
          max_amount: Number(values.maxAmount),
          min_period: Number(values.minPeriod),
          max_period: Number(values.maxPeriod),
          parts: []
        }),

        // part_payment_allowed:values.partPaymentAllowed !='yes',
        // ...(values.partPaymentAllowed === "yes" && {
        //   min_amount: Number(values.minAmount),
        //   max_amount: Number(values.maxAmount),
        //   min_period: Number(values.minPeriod),
        //   max_period: Number(values.maxPeriod),
        //   parts: []
        // }),
      };
      console.log("==payload", payload)


      await create_payment(payload);
      show_toast("Part Payment saved successfully!", "success");
      refetchProductDetails?.()
      resetForm();
    } catch (error) {
      console.error(error)
      show_toast("Failed to save part payment. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, status }) => (
          <Form className="mb-3">
            {status?.success && (
              <div className="alert alert-success" role="alert">
                {status.success}
              </div>
            )}
            {status?.error && (
              <div className="alert alert-danger" role="alert">
                {status.error}
              </div>
            )}
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold">Is part payment allowed?</label>
                <div className="d-flex gap-3" style={{ gap: "1rem" }}>
                  <div className="form-check">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="partPaymentAllowed"
                      id="partPaymentYes"
                      value="yes"
                    />
                    <label className="form-check-label" htmlFor="partPaymentYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <Field
                      className="form-check-input"
                      type="radio"
                      name="partPaymentAllowed"
                      id="partPaymentNo"
                      value="no"
                    />
                    <label className="form-check-label" htmlFor="partPaymentNo">
                      No
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  name="partPaymentAllowed"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>
            </div>

            {values.partPaymentAllowed === "yes" && (
              <>
                <div className="row g-3 mt-3">
                  <div className="col-md-4">
                    <label htmlFor="minAmount" className="form-label fw-semibold">
                      Minimum Amount
                    </label>
                    <Field name="minAmount" type="number" className="form-control" />
                    <ErrorMessage
                      name="minAmount"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                    <div className="col-md-4">
                    <label htmlFor="maxAmount" className="form-label fw-semibold">
                      Maximum Amount
                    </label>
                    <Field name="maxAmount" type="number" className="form-control" />
                    <ErrorMessage
                      name="maxAmount"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="minPeriod" className="form-label fw-semibold">
                      Minimum Period (Days)
                    </label>
                    <Field name="minPeriod" type="number" className="form-control" />
                    <ErrorMessage
                      name="minPeriod"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="maxPeriod" className="form-label fw-semibold">
                      Maximum Period (Days)
                    </label>
                    <Field name="maxPeriod" type="number" className="form-control" />
                    <ErrorMessage
                      name="maxPeriod"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </div>

                {/* Parts Section */}
                <div className="mt-4">
                  <label className="form-label fw-semibold">Parts</label>
                  <FieldArray name="parts">
                    {({ push, remove, form }) => (
                      <div>
                        {form.values.parts.map((_, index) => (
                          <div key={index} className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label htmlFor={`parts.${index}.period`} className="form-label">
                                Period (Days)
                              </label>
                              <Field
                                name={`parts.${index}.period`}
                                type="number"
                                className="form-control"
                              />
                              <ErrorMessage
                                name={`parts.${index}.period`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>
                            <div className="col-md-4">
                              <label htmlFor={`parts.${index}.value`} className="form-label">
                                Value (%)
                              </label>
                              <Field
                                name={`parts.${index}.value`}
                                type="number"
                                className="form-control"
                              />
                              <ErrorMessage
                                name={`parts.${index}.value`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => push({ period: "", value: "" })}
                        >
                          Add Part
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>
              </>
            )}

            {/* Save Button */}
            <div className="d-flex justify-content-end mt-4">
              <button
                type="submit"
                className="btn btn-success px-4 py-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Part Payment"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}